import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/shared/auth/AuthProvider'
import { PERM } from '@/shared/auth/types'
import type { NavBadges } from '@/app/nav'
import { fetchPortalSummary, portalKeys } from '@/features/portal/api'
import { fetchUnreadCount, notificationKeys } from '@/features/notifications/api'

/**
 * The Billing sidebar's counts.
 *
 * Both come from queries other screens already run -- the landing page's
 * summary and the bell's unread count -- under the same keys, so the sidebar
 * adds no request of its own and can never disagree with the number shown
 * beside it on Beranda or on the bell.
 */
export function useBillingBadges(): NavBadges {
  const { can } = useAuth()
  const canApprove = can(PERM.BILLING_APPROVAL)

  const summary = useQuery({
    queryKey: portalKeys.summary,
    queryFn: fetchPortalSummary,
    staleTime: 60_000,
    // No approval rights, no approval count: the summary would come back
    // without one anyway, and the request is the most expensive one we make.
    enabled: canApprove,
  })

  // The bell polls this key; the sidebar only reads along.
  const unread = useQuery({
    queryKey: notificationKeys.unread,
    queryFn: fetchUnreadCount,
    staleTime: 30_000,
  })

  const badges: NavBadges = {}
  const waiting = canApprove ? summary.data?.waitingOnMe : undefined
  if (waiting) badges['/billing/approvals'] = { count: waiting, tone: 'attention' }
  const unreadCount = unread.data?.unread
  if (unreadCount) badges['/billing/notifications'] = { count: unreadCount, tone: 'info' }
  return badges
}
